import React, { useEffect, useState } from "react";
import api from "../api";
import {
  KeyRound, Eye, EyeOff, ShieldCheck, AlertTriangle, Check,
} from "lucide-react";

/** Admin password change form. Hash is stored in MongoDB (settings.adminPasswordHash). */
export default function PasswordChange() {
  const [cur, setCur] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showCur, setShowCur] = useState(false);
  const [showNext, setShowNext] = useState(false);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState({ type: "", text: "" });

  const reset = () => { setCur(""); setNext(""); setConfirm(""); };

  const strength = (() => {
    if (!next) return null;
    let score = 0;
    if (next.length >= 12) score++;
    if (/[A-Z]/.test(next) && /[a-z]/.test(next)) score++;
    if (/\d/.test(next)) score++;
    if (/[^A-Za-z0-9]/.test(next)) score++;
    if (next.length >= 16) score++;
    const labels = ["Debole", "Debole", "Discreta", "Buona", "Ottima", "Eccellente"];
    const colors = ["#b91c1c","#b91c1c","#b45309","#15803d","#0f766e","#0f766e"];
    return { label: labels[score], color: colors[score], pct: (score+1) * (100/6) };
  })();

  const submit = async (e) => {
    e?.preventDefault?.();
    setMsg({ type:"", text:"" });
    if (next.length < 8) {
      setMsg({ type:"err", text:"La nuova password deve essere di almeno 8 caratteri." });
      return;
    }
    if (next !== confirm) {
      setMsg({ type:"err", text:"La conferma non coincide con la nuova password." });
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post("/auth/password",
        { currentPassword: cur, newPassword: next });
      setMsg({ type:"ok", text: data.message || "Password aggiornata." });
      reset();
    } catch (err) {
      setMsg({ type:"err", text: err.response?.data?.detail || "Errore aggiornamento password." });
    } finally { setSaving(false); }
  };

  return (
    <div className="card mb-4" data-testid="password-change-card">
      <div className="h-section mb-2" style={{display:"flex",alignItems:"center",gap:8}}>
        <KeyRound size={18}/> Cambio password admin
      </div>
      <div className="text-small text-muted mb-4" style={{lineHeight:1.55}}>
        Il nuovo hash bcrypt viene salvato in database e prende effetto al prossimo login,
        senza redeploy. Tieni la password al sicuro: non è recuperabile.
      </div>

      <form onSubmit={submit} autoComplete="off">
        <label className="label">Password attuale</label>
        <div style={{position:"relative", marginBottom:12}}>
          <input
            type={showCur ? "text" : "password"}
            className="input"
            value={cur}
            onChange={e=>setCur(e.target.value)}
            autoComplete="current-password"
            required
            data-testid="pw-current"
            style={{paddingRight:38}}/>
          <button type="button"
            onClick={()=>setShowCur(v=>!v)}
            style={{position:"absolute", right:8, top:8, background:"none", border:0, cursor:"pointer", color:"var(--mv-text-muted)"}}
            tabIndex={-1}>
            {showCur ? <EyeOff size={16}/> : <Eye size={16}/>}
          </button>
        </div>

        <label className="label">Nuova password (min. 8 caratteri)</label>
        <div style={{position:"relative", marginBottom:6}}>
          <input
            type={showNext ? "text" : "password"}
            className="input"
            value={next}
            onChange={e=>setNext(e.target.value)}
            autoComplete="new-password"
            required
            minLength={8}
            data-testid="pw-new"
            style={{paddingRight:38}}/>
          <button type="button"
            onClick={()=>setShowNext(v=>!v)}
            style={{position:"absolute", right:8, top:8, background:"none", border:0, cursor:"pointer", color:"var(--mv-text-muted)"}}
            tabIndex={-1}>
            {showNext ? <EyeOff size={16}/> : <Eye size={16}/>}
          </button>
        </div>
        {strength && (
          <div style={{marginBottom:12}}>
            <div style={{height:5, background:"var(--mv-border-light)", borderRadius:3, overflow:"hidden"}}>
              <div style={{
                width:`${strength.pct}%`,
                height:"100%",
                background: strength.color,
                transition:"width 0.2s",
              }}/>
            </div>
            <div className="text-small" style={{color: strength.color, marginTop:3, fontWeight:600}}>
              Robustezza: {strength.label}
            </div>
          </div>
        )}

        <label className="label">Conferma nuova password</label>
        <input
          type={showNext ? "text" : "password"}
          className="input"
          value={confirm}
          onChange={e=>setConfirm(e.target.value)}
          autoComplete="new-password"
          required
          data-testid="pw-confirm"/>

        {msg.text && (
          <div className="card mt-3" style={{
            padding:10,
            background: msg.type==="ok" ? "#dcfce7" : "#fef2f2",
            borderLeft: `3px solid ${msg.type==="ok" ? "#15803d" : "#b91c1c"}`,
          }}>
            <div className="text-small" style={{
              color: msg.type==="ok" ? "#166534" : "#7f1d1d",
              display:"flex", alignItems:"center", gap:6,
            }} data-testid="pw-msg">
              {msg.type==="ok" ? <Check size={14}/> : <AlertTriangle size={14}/>}
              {msg.text}
            </div>
          </div>
        )}

        <div className="mt-3">
          <button type="submit" className="btn btn-primary" disabled={saving || !cur || !next || !confirm}
            data-testid="pw-submit">
            {saving ? <span className="spinner"/> : <><ShieldCheck size={14}/> Aggiorna password</>}
          </button>
        </div>
      </form>
    </div>
  );
}
